/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useEffect, useState } from 'react';
import { Backdrop, CircularProgress } from '@mui/material';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { Palette, Tokens } from '@jda/lui-common-component-library-mui5';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { ByAccountInfo, FeatureFlagsProvider, isIFrame } from '@jda/lui-portal-utilities';
import { AuthService } from '../byauth/authService';
import LandingScreen from './landingScreen/landingScreen';

export const LoadingOverlay = () => (
  <Backdrop transitionDuration={0} open style={{ backgroundColor: Palette.colors.common.white, zIndex: 9000 }}>
    <CircularProgress
      data-testid="lui-portal-circular-progess-loader"
      variant="indeterminate"
      size="3rem"
      style={{ color: Tokens.colors.lightTheme.primaryMain }}
    />
  </Backdrop>
);

export const MainApp = (props: any) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, accounts } = useMsal();

  const [handlingLogin, setHandlingLogin] = useState(false);
  const [showed, setShowed] = useState(false);

  const handleLogin = async () => {
    try {
      setHandlingLogin(false);
      await AuthService.getInstance().login();
    } catch (error) {
      console.error('Got error back');
      console.error(error);
    }
  };

  useEffect(() => {
    let urlIdpHint = AuthService.getInstance()
      .getUrlIdpHint()
      ?.trim();

    if (urlIdpHint && urlIdpHint !== '') {
      const urlDomainHint = AuthService.getInstance()
        .getUrlDomainHint()
        ?.trim();
      const initialHandlingLogin = Boolean(
        (urlDomainHint && urlDomainHint !== '') || (urlIdpHint && urlIdpHint !== '')
      );
      setHandlingLogin(initialHandlingLogin);
    } else {
      const urlDomainHint = AuthService.getInstance()
        .getUrlDomainHint()
        ?.trim();
      const initialHandlingLogin = Boolean(urlDomainHint && urlDomainHint !== '');
      setHandlingLogin(initialHandlingLogin);
    }
  }, []);

  useEffect(() => {
    if (!showed && handlingLogin && inProgress === InteractionStatus.None && !isAuthenticated) {
      setShowed(true);
      handleLogin();
    }
  }, [inProgress, handlingLogin]);

  if (isAuthenticated && accounts.length >= 1) {
    return <React.Suspense fallback={<LoadingOverlay />}>{props.children}</React.Suspense>;
  } else if (inProgress !== InteractionStatus.HandleRedirect && inProgress !== InteractionStatus.Startup) {
    return <LandingScreen />;
  } else {
    return null;
  }
};

const Harness = (props: any) => {
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null | undefined>(undefined);
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();

  useEffect(() => {
    if (isAuthenticated && accounts.length >= 1) {
      setAccountInfo(AuthService.getInstance().getDefaultAccount());
    }
  }, [accounts, isAuthenticated]);

  return (
    <FeatureFlagsProvider
      apiKey={window.env.LUI_HARNESS_MFE_KEY}
      authInfo={
        accountInfo ||
        (({
          environment: '',
          homeAccountId: '',
          localAccountId: '',
          tenantId: '',
          username: '',
          idTokenClaims: { sub: 'PORTAL_LOGIN' }
        } as unknown) as ByAccountInfo)
      }
    >
      <MainApp {...props} />
    </FeatureFlagsProvider>
  );
};

function BYADWrapper(props: any) {
  const authProvider = AuthService.getInstance().getAuthProvider();
  if (isIFrame()) {
    return <React.Fragment>{props.children}</React.Fragment>;
  } else {
    return (
      <MsalProvider instance={authProvider}>
        <Harness {...props} />
      </MsalProvider>
    );
  }
}

export default BYADWrapper;
